import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthState {
 user: User | null;
 session: Session | null;
 loading: boolean;
 error: string | null;
}

interface SupabaseAuthContextValue extends AuthState {
 refreshSession: () => Promise<void>;
 sessionHealth: 'valid' | 'invalid' | 'refreshing' | 'unknown';
 validateSession: () => Promise<boolean>;
}

const SupabaseAuthContext = createContext<SupabaseAuthContextValue | undefined>(undefined);

const initialState: AuthState = {
 user: null,
 session: null,
 loading: true,
 error: null
};

export const SupabaseAuthProvider = ({ children }: { children: ReactNode }) => {
 const [state, setState] = useState<AuthState>(initialState);
 const [sessionHealth, setSessionHealth] = useState<'valid' | 'invalid' | 'refreshing' | 'unknown'>('unknown');
 const isMounted = useRef(true);
 const refreshAttempts = useRef(0);
 const MAX_REFRESH_ATTEMPTS = 3;

 const validateSession = useCallback(async (): Promise<boolean> => {
 console.log('🔍 [SupabaseAuth] Validating session...');
 
 for (let attempt = 1; attempt <= MAX_REFRESH_ATTEMPTS; attempt++) {
 try {
 const { data, error } = await supabase.auth.getSession();
 
 if (!error && data.session) {
 console.log(`✅ [SupabaseAuth] Session valid on attempt ${attempt}`);
 setSessionHealth('valid');
 return true;
 }
 
 if (attempt < MAX_REFRESH_ATTEMPTS) {
 console.log(`⏳ [SupabaseAuth] Attempt ${attempt}/${MAX_REFRESH_ATTEMPTS} - retrying in 500ms...`);
 await new Promise(resolve => setTimeout(resolve, 500));
 }
 } catch (err) {
 console.error(`❌ [SupabaseAuth] Validation attempt ${attempt} failed:`, err);
 }
 }
 
 console.error('❌ [SupabaseAuth] Session validation failed after all attempts');
 setSessionHealth('invalid');
 return false;
 }, [MAX_REFRESH_ATTEMPTS]);

 const refreshSession = useCallback(async () => {
 if (!isMounted.current) return;
 setSessionHealth('refreshing');
 setState((prev) => ({ ...prev, loading: true, error: null }));

 try {
 // First attempt: regular getSession
 let { data, error } = await supabase.auth.getSession();

 // If session is invalid, try refreshing
 if ((error || !data.session) && refreshAttempts.current < MAX_REFRESH_ATTEMPTS) {
 console.log(`🔄 [SupabaseAuth] Session invalid, attempting refresh (${refreshAttempts.current + 1}/${MAX_REFRESH_ATTEMPTS})...`);
 refreshAttempts.current++;
 
 const refreshResult = await supabase.auth.refreshSession();
 data = refreshResult.data;
 error = refreshResult.error;
 }

 if (!isMounted.current) return;

 if (error) {
 console.error("❌ [SupabaseAuth] Error fetching session:", error);
 setSessionHealth('invalid');
 setState({
 user: null,
 session: null,
 loading: false,
 error: error.message ?? "Failed to fetch session"
 });
 return;
 }

 if (data.session) {
 refreshAttempts.current = 0; // Reset on success
 console.log('✅ [SupabaseAuth] Session refreshed successfully');
 setSessionHealth('valid');
 } else {
 setSessionHealth('invalid');
 }

 setState({
 user: data.session?.user ?? null,
 session: data.session ?? null,
 loading: false,
 error: null
 });
 } catch (err) {
 if (!isMounted.current) return;
 console.error("❌ [SupabaseAuth] Error checking session:", err);
 setSessionHealth('invalid');
 setState({
 user: null,
 session: null,
 loading: false,
 error: err instanceof Error ? err.message : "Failed to fetch session"
 });
 }
 }, [MAX_REFRESH_ATTEMPTS]);

 useEffect(() => {
 isMounted.current = true;

 const { data } = supabase.auth.onAuthStateChange((_event, session) => {
 if (!isMounted.current) return;

 setState({
 user: session?.user ?? null,
 session: session ?? null,
 loading: false,
 error: null
 });
 });

 refreshSession();

 return () => {
 isMounted.current = false;
 data?.subscription.unsubscribe();
 };
 }, [refreshSession]);

 const value = useMemo(() => ({
 user: state.user,
 session: state.session,
 loading: state.loading,
 error: state.error,
 refreshSession,
 sessionHealth,
 validateSession
 }), [state, refreshSession, sessionHealth, validateSession]);

 return (
 <SupabaseAuthContext.Provider value={value}>
 {children}
 </SupabaseAuthContext.Provider>
 );
};

export const useSupabaseAuth = () => {
 const context = useContext(SupabaseAuthContext);
 if (!context) {
 throw new Error("useSupabaseAuth must be used within a SupabaseAuthProvider");
 }
 return context;
};
